// Clipboard consumers keep inline styles and drop the app's stylesheets, and
// most of them also ignore CSS on SVG. Paint is copied onto each element, and
// SVG strokes/fills are written as presentation attributes. A chart figure
// (plot, legend, and caption) is then flattened to one PNG so the pasted
// chart is a single image rather than a pile of pieces.

const SVG_NS = 'http://www.w3.org/2000/svg';

// Only these ride along from a parent. display, padding, borders, and flex
// layout do not, so they have to be written even when a child matches its
// parent -- otherwise a legend row collapses once its class is gone.
const INHERITED_PROPS = new Set([
  'color',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'font-variant-numeric',
  'letter-spacing',
  'line-height',
  'text-transform',
  'text-align',
  'text-decoration-line',
  'white-space',
]);

const PAINT_PROPS = [
  ...INHERITED_PROPS,
  'background-color',
  'border-top-width',
  'border-right-width',
  'border-bottom-width',
  'border-left-width',
  'border-top-style',
  'border-right-style',
  'border-bottom-style',
  'border-left-style',
  'border-top-color',
  'border-right-color',
  'border-bottom-color',
  'border-left-color',
  'border-top-left-radius',
  'border-top-right-radius',
  'border-bottom-right-radius',
  'border-bottom-left-radius',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'display',
  'vertical-align',
  'opacity',
  'max-width',
  'column-gap',
  'row-gap',
  'gap',
  'align-items',
  'justify-content',
  'flex-wrap',
  'flex-direction',
  'flex-shrink',
];

const SVG_SHAPES = new Set([
  'path',
  'line',
  'circle',
  'rect',
  'ellipse',
  'polyline',
  'polygon',
  'text',
  'tspan',
]);

const SVG_PAINT = [
  'fill',
  'stroke',
  'stroke-width',
  'stroke-dasharray',
  'stroke-dashoffset',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-opacity',
  'fill-opacity',
  'opacity',
  'font-size',
  'font-family',
  'font-weight',
  'color',
  'text-anchor',
  'dominant-baseline',
];

const BLOCKS = new Set([
  'DIV',
  'P',
  'LI',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'SECTION',
  'FIGCAPTION',
  'UL',
  'OL',
  'FIGURE',
  'TR',
]);

function isSkippable(prop, value, computed) {
  if (!value) return true;
  if (prop === 'opacity') return value === '1';
  if (prop === 'font-style' || prop === 'font-variant-numeric') {
    return value === 'normal';
  }
  if (prop === 'font-weight') return clipboardFontWeight(value) !== 'bold';
  if (prop === 'letter-spacing') return value === 'normal' || value === '0px';
  if (prop === 'line-height') return value === 'normal';
  if (prop === 'text-transform' || prop === 'text-decoration-line') {
    return value === 'none';
  }
  if (prop === 'white-space') return value === 'normal';
  if (prop === 'vertical-align') return value === 'baseline';
  if (prop === 'background-color') {
    return value === 'transparent' || value === 'rgba(0, 0, 0, 0)';
  }
  if (prop.startsWith('margin-') || prop.startsWith('padding-')) {
    return value === '0px';
  }
  if (prop.endsWith('-radius')) return value === '0px';
  if (prop.endsWith('-style') && prop.startsWith('border-')) {
    return value === 'none';
  }
  if (
    prop.startsWith('border-') &&
    (prop.endsWith('-width') || prop.endsWith('-color'))
  ) {
    const side = prop.split('-')[1];
    return (
      value === '0px' ||
      computed.getPropertyValue(`border-${side}-style`) === 'none'
    );
  }
  if (prop === 'max-width') return value === 'none';
  if (prop === 'gap' || prop === 'column-gap' || prop === 'row-gap') {
    return value === 'normal' || value === '0px';
  }
  if (prop === 'flex-shrink') return value === '1';
  if (prop === 'flex-wrap') return value === 'nowrap';
  if (prop === 'flex-direction') return value === 'row';
  if (prop === 'align-items' || prop === 'justify-content') {
    return value === 'normal';
  }
  return false;
}

// Computed font-weight is always a number, even when the stylesheet says
// `bold`. Paste targets often ignore 600/650/700 and keep the text regular.
function clipboardFontWeight(value) {
  if (value === 'bold' || value === 'bolder') return 'bold';
  const weight = Number.parseInt(value, 10);
  if (Number.isFinite(weight)) return weight >= 600 ? 'bold' : 'normal';
  return 'normal';
}

function isPaintedDot(source, computed) {
  if (source.childNodes.length > 0) return false;
  const width = parseFloat(computed.width);
  const height = parseFloat(computed.height);
  return width > 0 && width <= 32 && height > 0 && height <= 32;
}

function applyPaint(source, clone, computed, parentComputed, isRoot) {
  PAINT_PROPS.forEach((prop) => {
    const raw = computed.getPropertyValue(prop);
    const value = prop === 'font-weight' ? clipboardFontWeight(raw) : raw;
    if (isSkippable(prop, value, computed)) return;
    if (
      !isRoot &&
      INHERITED_PROPS.has(prop) &&
      parentComputed &&
      parentComputed.getPropertyValue(prop) === value
    ) {
      return;
    }
    clone.style.setProperty(prop, value);
  });

  if (isPaintedDot(source, computed)) {
    clone.style.width = computed.width;
    clone.style.height = computed.height;
  }
}

function applyLayout(source, clone, computed) {
  const position = computed.getPropertyValue('position');
  const tag = source.tagName.toLowerCase();
  const positioned =
    position === 'absolute' || position === 'relative' || position === 'fixed';
  const sized = positioned || tag === 'svg' || tag === 'img';

  if (positioned) {
    clone.style.position = position;
  }
  if (computed.getPropertyValue('box-sizing') === 'border-box') {
    clone.style.boxSizing = 'border-box';
  }
  if (!sized) return;

  ['top', 'right', 'bottom', 'left', 'width', 'height'].forEach((prop) => {
    const value = computed.getPropertyValue(prop);
    if (!value || value === 'auto') return;
    clone.style.setProperty(prop, value);
  });
}

const SVG_TEXT_PROPS = new Set([
  'font-size',
  'font-family',
  'font-weight',
  'text-anchor',
  'dominant-baseline',
  'color',
]);

function applySvgPaint(clone, computed) {
  const tag = clone.tagName.toLowerCase();
  SVG_PAINT.forEach((prop) => {
    if (SVG_TEXT_PROPS.has(prop) && tag !== 'text' && tag !== 'tspan') return;
    const raw = computed.getPropertyValue(prop);
    const value = prop === 'font-weight' ? clipboardFontWeight(raw) : raw;
    if (!value) return;
    if (prop === 'opacity' && value === '1') return;
    if (prop === 'font-weight' && value !== 'bold') return;
    clone.setAttribute(prop, value);
    clone.style.setProperty(prop, value);
  });
}

function prepareSvg(clone, computed) {
  clone.setAttribute('xmlns', SVG_NS);
  const width = Math.round(parseFloat(computed.width));
  const height = Math.round(parseFloat(computed.height));
  if (!width || !height) return;
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  if (!clone.getAttribute('viewBox')) {
    clone.setAttribute('viewBox', `0 0 ${width} ${height}`);
  }
}

function inlineTree(
  source,
  clone,
  layout,
  parentComputed = null,
  isRoot = true
) {
  if (!source || !clone || source.nodeType !== 1 || clone.nodeType !== 1) {
    return;
  }

  const computed = window.getComputedStyle(source);
  applyPaint(source, clone, computed, parentComputed, isRoot);

  const tag = source.tagName.toLowerCase();
  if (layout) applyLayout(source, clone, computed);
  if (tag === 'svg') prepareSvg(clone, computed);
  if (SVG_SHAPES.has(tag)) applySvgPaint(clone, computed);
  if (tag === 'img') {
    const src = source.currentSrc || source.getAttribute('src');
    if (src) {
      try {
        clone.setAttribute('src', new URL(src, window.location.href).href);
      } catch (err) {
        clone.setAttribute('src', src);
      }
    }
  }

  const sourceChildren = source.children;
  const cloneChildren = clone.children;
  const count = Math.min(sourceChildren.length, cloneChildren.length);
  for (let i = 0; i < count; i += 1) {
    inlineTree(sourceChildren[i], cloneChildren[i], layout, computed, false);
  }
}

function prune(root) {
  root
    .querySelectorAll(
      'button, script, style, [data-copy-exclude], .visx-tooltip'
    )
    .forEach((node) => node.remove());
}

function stripHooks(root) {
  [root, ...root.querySelectorAll('*')].forEach((el) => {
    el.removeAttribute('class');
    el.removeAttribute('id');
    [...el.attributes].forEach((attr) => {
      if (
        attr.name.startsWith('data-') ||
        attr.name.startsWith('aria-') ||
        attr.name === 'role'
      ) {
        el.removeAttribute(attr.name);
      }
    });
  });
}

function styleClone(source, { layout }) {
  const clone = source.cloneNode(true);
  inlineTree(source, clone, layout);
  prune(clone);
  stripHooks(clone);
  return clone;
}

function plainText(root) {
  let out = '';

  function walk(node) {
    if (node.nodeType === 3) {
      out += node.textContent.replace(/\s+/g, ' ');
      return;
    }
    if (node.nodeType !== 1) return;
    if (node.tagName === 'IMG') {
      if (node.alt) out += `${node.alt}\n`;
      return;
    }
    const block = BLOCKS.has(node.tagName);
    if (block && out && !out.endsWith('\n')) out += '\n';
    [...node.childNodes].forEach(walk);
    if (block && out && !out.endsWith('\n')) out += '\n';
  }

  walk(root);
  return out
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function figureImage({ dataUrl, width, height, alt }) {
  const img = document.createElement('img');
  img.src = dataUrl;
  img.alt = alt || 'Chart';
  img.setAttribute('width', String(width));
  img.setAttribute('height', String(height));
  img.style.cssText =
    'display:block;max-width:100%;height:auto;margin:0.75rem 0;border:0;';
  return img;
}

function canRasterize() {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext && canvas.getContext('2d'));
  } catch (err) {
    return false;
  }
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const timer = window.setTimeout(() => {
      reject(new Error('Chart image timed out'));
    }, 4000);
    image.onload = () => {
      window.clearTimeout(timer);
      resolve(image);
    };
    image.onerror = () => {
      window.clearTimeout(timer);
      reject(new Error('Chart image failed'));
    };
    image.src = url;
  });
}

function visibleColor(value) {
  if (!value || value === 'transparent' || value === 'rgba(0, 0, 0, 0)') {
    return null;
  }
  return value;
}

function chartSvgIndex(figure) {
  const svgs = [...figure.querySelectorAll('svg')];
  let best = 0;
  let bestArea = -1;
  svgs.forEach((svg, index) => {
    const rect = svg.getBoundingClientRect();
    const area = rect.width * rect.height;
    if (area > bestArea) {
      best = index;
      bestArea = area;
    }
  });
  return svgs.length ? best : -1;
}

function legendEntries(figure) {
  const legend = figure.querySelector('[data-chart-legend]');
  if (!legend) return [];
  return [...legend.children]
    .map((item) => {
      const label = item
        .querySelector('[data-chart-legend-label]')
        ?.textContent?.trim();
      if (!label) return null;
      const dot = [...item.querySelectorAll('span')].find((span) =>
        visibleColor(window.getComputedStyle(span).backgroundColor)
      );
      const line = item.querySelector('line, path');
      const color =
        (dot && visibleColor(window.getComputedStyle(dot).backgroundColor)) ||
        (line && visibleColor(window.getComputedStyle(line).stroke)) ||
        '#6c757d';
      return { label, color, dashed: Boolean(line) };
    })
    .filter(Boolean);
}

function layoutLegend(ctx, entries, maxWidth) {
  const rows = [];
  let row = [];
  let rowWidth = 0;
  entries.forEach((entry) => {
    const width = 16 + ctx.measureText(entry.label).width;
    if (row.length && rowWidth + 16 + width > maxWidth) {
      rows.push(row);
      row = [];
      rowWidth = 0;
    }
    if (row.length) rowWidth += 16;
    row.push({ ...entry, width });
    rowWidth += width;
  });
  if (row.length) rows.push(row);
  return rows;
}

function drawSwatch(ctx, entry, x, y) {
  ctx.save();
  ctx.strokeStyle = entry.color;
  ctx.fillStyle = entry.color;
  if (entry.dashed) {
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 12, y);
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.arc(x + 4, y, 3.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

async function chartImage(styledSvg) {
  if (!styledSvg) return null;
  const clone = styledSvg.cloneNode(true);
  const width = Math.round(parseFloat(clone.getAttribute('width')));
  const height = Math.round(parseFloat(clone.getAttribute('height')));
  if (!width || !height) return null;
  clone.setAttribute('xmlns', SVG_NS);
  if (!clone.getAttribute('viewBox')) {
    clone.setAttribute('viewBox', `0 0 ${width} ${height}`);
  }
  const background = document.createElementNS(SVG_NS, 'rect');
  background.setAttribute('width', '100%');
  background.setAttribute('height', '100%');
  background.setAttribute('fill', '#ffffff');
  clone.insertBefore(background, clone.firstChild);

  const xml = new XMLSerializer().serializeToString(clone);
  const url = URL.createObjectURL(
    new Blob([xml], { type: 'image/svg+xml;charset=utf-8' })
  );
  try {
    const image = await loadImage(url);
    return { image, width, height };
  } catch (err) {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Chrome paints a blank bitmap when an SVG image contains HTML
// (foreignObject), so the plot is drawn as SVG and the legend and caption
// are drawn onto the same canvas. The figure's words stay inside that PNG.
export async function rasterizeFigureElement(styledFigure, sourceFigure) {
  if (!canRasterize() || !sourceFigure) return null;
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch (err) {
      // Fall back to whatever faces are already available.
    }
  }

  const index = chartSvgIndex(sourceFigure);
  const styledSvg =
    index >= 0 ? styledFigure.querySelectorAll('svg')[index] : null;
  const plot = await chartImage(styledSvg);
  const entries = legendEntries(sourceFigure);
  const caption = sourceFigure.querySelector('figcaption')?.textContent?.trim();
  if (!plot && entries.length === 0 && !caption) return null;

  const figureStyle = window.getComputedStyle(sourceFigure);
  const family = figureStyle.fontFamily || 'sans-serif';
  const padX = 12;
  const padY = 10;
  const plotW = plot?.width || 0;
  const plotH = plot?.height || 0;
  const width = Math.max(
    plotW + padX * 2,
    Math.round(sourceFigure.getBoundingClientRect().width) || 0,
    220
  );

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.font = `12px ${family}`;
  const rows = layoutLegend(ctx, entries, width - padX * 2);
  const legendHeight = rows.length ? rows.length * 18 + 8 : 0;
  const captionHeight = caption ? 22 : 0;
  const height = padY + plotH + legendHeight + captionHeight + padY;
  const scale = 2;
  canvas.width = width * scale;
  canvas.height = height * scale;
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  const border = visibleColor(figureStyle.borderTopColor) || '#d5dde3';
  ctx.strokeStyle = border;
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, width - 1, height - 1);

  if (plot) {
    ctx.drawImage(plot.image, padX, padY, plotW, plotH);
  }

  let y = padY + plotH + (rows.length ? 16 : 0);
  rows.forEach((row) => {
    let x = padX;
    row.forEach((entry) => {
      drawSwatch(ctx, entry, x, y);
      ctx.font = `12px ${family}`;
      ctx.fillStyle = '#333333';
      ctx.textBaseline = 'middle';
      ctx.fillText(entry.label, x + 16, y);
      x += entry.width + 16;
    });
    y += 18;
  });

  if (caption) {
    ctx.font = `bold 13px ${family}`;
    ctx.fillStyle = '#333333';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(caption, width / 2, height - padY - 8);
  }

  return {
    dataUrl: canvas.toDataURL('image/png'),
    width,
    height,
    alt: caption || 'Chart',
  };
}

export async function buildBriefingClipboard(
  source,
  { rasterizeFigure = rasterizeFigureElement } = {}
) {
  const clone = styleClone(source, { layout: false });
  // The generating state fades the whole briefing. The copy should read at
  // full strength.
  clone.style.removeProperty('opacity');

  // Pair against the live figures that survive pruning. A tooltip can sit
  // inside a chart, but it is not a figure and is left out of the bitmap.
  const liveFigures = [...source.querySelectorAll('figure')].filter(
    (figure) => !figure.closest('[data-copy-exclude], button, .visx-tooltip')
  );
  const copyFigures = [...clone.querySelectorAll('figure')];

  for (let i = 0; i < copyFigures.length; i += 1) {
    const live = liveFigures[i];
    if (!live) continue;
    let image = null;
    try {
      const styled = styleClone(live, { layout: true });
      image = await rasterizeFigure(styled, live);
    } catch (err) {
      image = null;
    }
    if (!image?.dataUrl) continue;
    copyFigures[i].replaceWith(figureImage(image));
  }

  // After figures are images, so axis labels and legend names are not also
  // dumped out as text.
  return { html: clone.outerHTML, text: plainText(clone) };
}

function copyViaSelection(html) {
  const host = document.createElement('div');
  host.setAttribute('contenteditable', 'true');
  host.style.cssText = 'position:fixed;left:-10000px;top:0;';
  host.innerHTML = html;
  document.body.appendChild(host);
  const range = document.createRange();
  range.selectNodeContents(host);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
  const ok = document.execCommand('copy');
  selection.removeAllRanges();
  host.remove();
  if (!ok) {
    throw new Error('Could not copy the briefing');
  }
}

// clipboard.write has to be called in the click turn. The HTML blob is a
// promise so chart rasterizing can finish without losing that gesture.
export function copyBriefingContent(source) {
  const pending = buildBriefingClipboard(source);

  if (
    !navigator.clipboard?.write ||
    typeof window.ClipboardItem === 'undefined'
  ) {
    return pending.then(({ html }) => copyViaSelection(html));
  }

  try {
    return navigator.clipboard
      .write([
        new window.ClipboardItem({
          'text/html': pending.then(
            ({ html }) => new Blob([html], { type: 'text/html' })
          ),
          'text/plain': pending.then(
            ({ text }) => new Blob([text], { type: 'text/plain' })
          ),
        }),
      ])
      .catch(() => pending.then(({ html }) => copyViaSelection(html)));
  } catch (err) {
    return pending.then(({ html }) => copyViaSelection(html));
  }
}
